import React, { useEffect, useState, useCallback } from "react";
import { TextField, TextFieldProps } from "@mui/material";
import { Autocomplete } from '@mui/material';
import SubjectModel, { SubjectData } from "../../core/api/manager/SubjectModel";

interface SubjectInputProps extends Omit<TextFieldProps, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
}

/**
 * A form input component that provides autocomplete functionality for email subjects
 */
export default function SubjectInput({ value, onChange, error, helperText, ...props }: SubjectInputProps) {
  const [subjects, setSubjects] = useState<SubjectData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSubjects = async () => {
      setLoading(true);
      try {
        const subjectList = await SubjectModel.listAll();
        setSubjects(subjectList);
      } catch (error) {
        console.error("Failed to fetch subjects:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, []);

  const handleAutocompleteChange = useCallback(
    (_event: React.ChangeEvent<{}>, newValue: string | null) => {
      onChange(newValue || "");
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (_event: React.ChangeEvent<{}>, newInputValue: string) => {
      onChange(newInputValue);
    },
    [onChange]
  );

  const renderInput = useCallback(
    (params: TextFieldProps) => (
      <TextField
        {...params}
        {...props}
        error={error}
        helperText={helperText}
        variant="outlined"
        fullWidth
      />
    ),
    [error, helperText, props]
  );

  return (
    <Autocomplete
      freeSolo
      options={subjects.map((subject) => subject.subject)}
      value={value}
      inputValue={value}
      onChange={handleAutocompleteChange}
      onInputChange={handleInputChange}
      loading={loading}
      renderInput={renderInput}
    />
  );
}
